import React, { createContext, useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { trackEvent } from '@fs/zion-analytics'
import zionDebug from '@fs/zion-debug'
import { NotFound, useQueryParams } from '@fs/zion-router'
import getPermission from '@fs/zion-permissions'
import { getFsAccountActivationStatus, getMemberData, getTreeData, getWorkingPersonContainer } from '../../lib/services'
import { memberDataMunge } from '../../lib/mungers'
import { publicAccountScreens } from '../../lib/helpers'
import useNavigate from '../../hooks/useNavigate'
import PageSpinner from '../common/PageSpinner'
import { initialState, reducer } from '../fnaReducer'
import FtteRedirectLayer from './FtteRedirectLayer'

const debug = zionDebug('FirstTimeTempleExperience:FtteProvider')

export const FtteContext = createContext()

/**
 * @description - Makes FTTE-related state available to all components in the experience.
 */
export default function FtteProvider({ children }) {
  const [displayNotFound, setDisplayNotFound] = useState(false)
  const [isLoadingWorkingPerson, setIsLoadingWorkingPerson] = useState(true)
  const [isLoadingMemberPerson, setIsLoadingMemberPerson] = useState(true)
  const [isAccountAssociatedWithUuid, setIsAccountAssociatedWithUuid] = useState(null)
  const { location, onPush, isTryDemo, useMockMatchData } = useNavigate()
  const { query } = useQueryParams()
  const { memberUuid, preventProdRedirect } = query ?? {}
  const [state, dispatch] = useReducer(reducer, initialState)

  // Update memberUuid in state if query parameter changes
  useEffect(() => {
    // NOTE: reading the `memberUuid` from provider state is easier to mock in tests than reading it from the URL
    dispatch({ type: 'SET_MEMBER_UUID', memberUuid })
  }, [dispatch, memberUuid])

  // Check whether member's UUID is already associated with a FamilySearch account
  useEffect(() => {
    // The call that `getFsAccountActivationStatus` makes has two purposes:
    // 1. Check to see if the member/helpee has a FS account associated with the UUID already to skip the account setup steps
    // 2. For the BE to store the helper-helpee relation in their DB. Other endpoints require this relation to be present to return data properly (or else they return 409 errors)

    if (isAccountAssociatedWithUuid === null) {
      const notFoundResponses = [400, 401, 403]
      getFsAccountActivationStatus(memberUuid)
        .then((accountStatus) => {
          dispatch({ type: 'SET_IS_ACCOUNT_ACTIVATED', isAccountActivated: accountStatus.fullyRegistered })

          const hasAccount = !!accountStatus.cisId
          if (hasAccount) {
            // FTTEGEN-465: If the member being helped already has a member account, we need to make a call to GET /tree/person to make sure they have a contributor ID
            // NOTE: We had issues with getTreeData returning 403 if the account was still public, so we're gating it behind a check for member account status.
            getPermission('FtMemberUiPermission').then(({ authorized }) => {
              if (authorized) getTreeData(memberUuid, '').then(() => setIsAccountAssociatedWithUuid(true))
              else setIsAccountAssociatedWithUuid(false)
            })
          } else {
            setIsAccountAssociatedWithUuid(false)
          }
        })
        .catch((error) => {
          debug(
            `The following error occurred getting the account activation status: ${JSON.stringify(error?.response)}`
          )
          if (error?.response?.status === 404) {
            // This means that there is no FamilySearch account associated with the UUID -- we want to send them to the /account page
            if (location.pathname !== '/account') onPush('/account')
          } else if (notFoundResponses.includes(error?.response?.status)) {
            setDisplayNotFound(true)
          } else {
            dispatch({
              type: 'SET_ERROR_TYPE',
              errorType: error?.response?.status >= 500 ? 'SYSTEM_DOWN' : 'ACCOUNT_ACTIVATION_STATUS',
            })
          }

          // Setting this to false stops the page from displaying the spinner
          setIsAccountAssociatedWithUuid(false)
        })
    }
  }, [dispatch, isAccountAssociatedWithUuid, location.pathname, memberUuid, onPush])

  // Fetch memberData
  useEffect(() => {
    if (preventProdRedirect !== 'true' && !publicAccountScreens.includes(location.pathname)) {
      getMemberData(memberUuid)
        .then((memberResponse) => {
          const memberData = memberDataMunge(memberResponse?.data)
          // Store member's information into local state
          dispatch({ type: 'SET_MEMBERSHIP_INFO', membershipInfo: memberData })
        })
        .catch((error) => {
          debug(
            `The following error occurred while getting the user's membership information: ${JSON.stringify(
              error?.response
            )}`
          )
          dispatch({
            type: 'SET_ERROR_TYPE',
            errorType: error?.response?.status >= 500 ? 'SYSTEM_DOWN' : 'READ_MEMBERSHIP_INFO',
          })
        })
        .finally(() => {
          setIsLoadingMemberPerson(false)
        })
    } else {
      setIsLoadingMemberPerson(false)
    }
  }, [dispatch, location.pathname, memberUuid, preventProdRedirect])

  // Fetch workingPersonContainer data from a incomplete, previous session.
  useEffect(() => {
    if (isAccountAssociatedWithUuid === null) return
    if (!isAccountAssociatedWithUuid) {
      setIsLoadingWorkingPerson(false)
      return
    }

    getWorkingPersonContainer(memberUuid)
      .catch((error) => {
        debug(`The following error occurred while getting the working person: ${JSON.stringify(error?.response)}`)
        dispatch({
          type: 'SET_ERROR_TYPE',
          errorType: error?.response?.status >= 500 ? 'SYSTEM_DOWN' : 'GET_WORKING_PERSON',
        })
      })
      .then((workingPersonContainer = {}) => {
        if (!workingPersonContainer?.metadata) workingPersonContainer.metadata = initialState.metadata
        dispatch({ type: 'SET_WORKING_PERSON_CONTAINER', ...workingPersonContainer })
      })
      .catch((error) => {
        debug(`The following error occurred in the FtteProvider: ${JSON.stringify(error?.response)}`)
        dispatch({ type: 'SET_ERROR_TYPE', errorCode: error?.response?.status, errorType: 'GENERAL_SYSTEM_ERROR' })
      })
      .finally(() => setIsLoadingWorkingPerson(false))

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on initial render
  }, [isAccountAssociatedWithUuid])

  const ftteTrackEvent = useCallback(
    (event) => {
      !isTryDemo && !useMockMatchData && trackEvent({ ...event, memberUUID: memberUuid })
    },
    [isTryDemo, useMockMatchData, memberUuid]
  )

  const values = useMemo(
    () => ({ dispatch, state, isLoading: isLoadingWorkingPerson || isLoadingMemberPerson, ftteTrackEvent }),
    [ftteTrackEvent, isLoadingMemberPerson, isLoadingWorkingPerson, state]
  )
  if (isLoadingWorkingPerson) return <PageSpinner /> // Prevent entire page from loading so the background doesn't flash and change
  if (displayNotFound) return <NotFound /> // Display the NotFound page for certain errors when checking the user's account
  return (
    <FtteContext.Provider value={values}>
      <FtteRedirectLayer isAccountAssociatedWithUuid={isAccountAssociatedWithUuid} providerValues={values}>
        {children}
      </FtteRedirectLayer>
    </FtteContext.Provider>
  )
}
